import ClientsChart from "@components/charts/ClientsChart";
import Button from "@components/common/Button";

import CustomAccordion from "@components/common/CustomAccordion";
import DatePicker from "@components/common/DatePicker";
import ListTable from "@components/common/ListTable";
import Pagination from "@components/common/pagination";
import SearchInput from "@components/common/SearchInput";
import SelectInput from "@components/common/SelectInput";
import DownloadIcon from "@components/icons/DownloadIcon";
import FileCsvIcon from "@components/icons/FileCsvIcon";
import { useIsDesktop, useIsMobile, useIsTablet } from "@components/mediaQuery/useIsMobile";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import {
	fetchDailyClientCountForVouchers,
	fetchVouchers,
	setDownloadVouchersData,
} from "@services/vesselService";
import {
	setDailyClientCountData,
	setLoadingDailyClientCount,
	setLoadingVouchers,
	setVouchersData,
} from "@store/modules/vesselsSlice";
import { categoryEnums } from "@utils/categoryEnums";
import { statusEnums } from "@utils/statusEnums";
import { FLAGS } from "config/growthbook/flags";
import { Tooltip } from "flowbite-react";
import { debounce } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

const Vouchers = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const controller = new AbortController();
	const signal = controller.signal;

	const { token, permissions } = useSelector((state) => state.auth.user);

	const [openAccordions, setOpenAccordions] = useState({
		"total-vouchers": true,
	});

	const [searchTerms, setSearchTerms] = useState({
		search: "",
		online: "",
		category: "",
	});
	const [searchTermsDate, setSearchTermsDate] = useState({
		fromDate: "",
		toDate: "",
	});
	const [errors, setErrors] = useState({ fromDate: "", toDate: "" });

	const {
		loading,
		data: vouchersData,
		pagination,
	} = useSelector((state) => state.modules.vessels.vouchers);

	const { loadingClients, data: clientdata } = useSelector(
		(state) => state.modules.vessels.vouchers?.dailyClients || {},
	);

	const { page, perPage, totalItems, totalPage: totalPages } = pagination;
	const [currentPage, setCurrentPage] = useState(1);

	const isMobile = useIsMobile();
	const isTablet = useIsTablet();
	const isDesktop = useIsDesktop();

	const vesselDetails = useSelector((state) => state.modules.vessels?.vesselDetails || {});

	const vesselId = useSelector((state) => state.modules.vessels?.selectedVessel?.id);

	const hasVouchers = vesselDetails?.modules?.some(
		(mod) => mod.name.toLowerCase() === "voucher services",
	);

	const [isLoadingDownload, setIsLoadingDownload] = useState(false);
	const [isSuccess, setIsSuccess] = useState(false);

	const hasOptimaNumber = useMemo(
		() => vouchersData?.some((item) => item?.optima_number != null),
		[vouchersData],
	);

	const hasCategory = useMemo(
		() => vouchersData?.some((item) => item?.category != null),
		[vouchersData],
	);
	const isvouchersPerDayVisible = useFeatureIsOn(
		FLAGS.vessels.VoucherService.vouchersPerDayVisible,
	);

	const toggleAccordion = (id) => {
		setOpenAccordions((prevState) => ({
			...prevState,
			[id]: !prevState[id],
		}));
	};

	const loadVouchersData = async (signal) => {
		dispatch(setLoadingVouchers(true));

		try {
			const result = await fetchVouchers(
				token,
				vesselId,
				dispatch,
				navigate,
				currentPage,
				perPage,
				searchTerms,
				1,
				signal,
			);

			dispatch(setVouchersData(result));
			dispatch(setLoadingVouchers(false));
		} catch (error) {
			if (error.name === "CanceledError") return;
			console.error("Failed to load vouchers:", error);
		} finally {
			dispatch(setLoadingVouchers(false));
		}
	};
	const loadDailyClientsData = async (signal) => {
		try {
			dispatch(setLoadingDailyClientCount(true));
			const result = await fetchDailyClientCountForVouchers(
				token,
				dispatch,
				vesselId,
				searchTermsDate.fromDate,
				searchTermsDate.toDate,
				signal,
			);
			dispatch(setDailyClientCountData(result));
		} catch (error) {
			console.error("Failed to load vouchers:", error);
		} finally {
			dispatch(setLoadingDailyClientCount(false));
		}
	};

	const debouncedFetch = useMemo(
		() =>
			debounce((signal) => {
				loadVouchersData(signal);
			}, 300),
		[token, vesselId, currentPage, perPage, searchTerms],
	);

	const debouncedDailyClientsFetch = useMemo(
		() =>
			debounce((signal) => {
				loadDailyClientsData(signal);
			}, 300),
		[token, vesselId, searchTermsDate],
	);

	useEffect(() => {
		const controller = new AbortController();

		debouncedFetch(controller.signal);

		return () => {
			debouncedFetch.cancel();
			controller.abort();
		};
	}, [debouncedFetch]);

	useEffect(() => {
		const controller = new AbortController();

		debouncedDailyClientsFetch(controller.signal);

		return () => {
			debouncedDailyClientsFetch.cancel();
			controller.abort();
		};
	}, [debouncedDailyClientsFetch]);

	const onClickDailyReport = () => {
		const reportUrl = vesselDetails?.download_report;

		if (typeof reportUrl === "string" && reportUrl) {
			window.open(reportUrl, "Daily-report");
		} else {
			console.log("No valid report link available");
		}
	};

	const isVisible = hasCategory && hasOptimaNumber;

	const columns = useMemo(
		() => [
			...(!isVisible
				? [
						{
							header: "Username",
							field: "username",
							sortable: false,
							content: (row) => <span>{row?.username || "—"}</span>,
						},
					]
				: []),
			...(hasOptimaNumber
				? [
						{
							header: "Optima number",
							field: "optima_number",
							sortable: false,
							content: (row) => <span>{row?.optima_number || "—"}</span>,
						},
					]
				: []),

			...(hasCategory
				? [
						{
							header: "Category",
							field: "category",
							sortable: false,
							content: (row) => <span>{row?.category || "—"}</span>,
						},
					]
				: []),
			{
				header: "Consumed",
				field: "consumed",
				sortable: false,
			},
			{
				header: "data usage",
				field: "data_usage",
				sortable: false,
			},
			{
				header: "Remaining",
				field: "remaining",
				sortable: false,
			},
			{
				header: "Status",
				field: "online",
				sortable: false,
				content: (data) => (
					<span
						className={`me-2 flex h-3 w-3 flex-shrink-0 rounded-full sm:h-4 sm:w-4 ${
							data?.online ? "bg-green-500" : "bg-red-500"
						}`}
					></span>
				),
			},
		],
		[isMobile, isTablet, hasOptimaNumber, hasCategory],
	);

	const handleRowClick = (id, name) => {
		null;
	};
	const handlePageChange = (newPage) => {
		if (newPage !== currentPage && newPage >= 1 && newPage <= totalPages) {
			setCurrentPage(newPage);
		}
	};

	const startItemIndex = (currentPage - 1) * perPage + 1;
	const endItemIndex = Math.min(currentPage * perPage, totalItems);
	const today = new Date();

	const handleSearchChange = (id, value) => {
		setSearchTerms((prevTerms) => {
			const updatedTerms = {
				...prevTerms,
				search: value,
			};
			setCurrentPage(1);

			return updatedTerms;
		});
	};

	const handleStatusSearchChange = (id, value, name) => {
		setSearchTerms((prevTerms) => {
			const updatedTerms = {
				...prevTerms,
				online: value,
			};
			setCurrentPage(1);
			return updatedTerms;
		});
	};
	const handleCategorySearchChange = (id, value, name) => {
		setSearchTerms((prevTerms) => {
			const updatedTerms = {
				...prevTerms,
				category: name,
			};
			setCurrentPage(1);
			return updatedTerms;
		});
	};
	const handleClearSearch = (id) => {
		setSearchTerms((prev) => ({
			...prev,
			[id]: "",
		}));

		setCurrentPage(1);
	};

	const handleDateChange = (date, field) => {
		if (!date) return;

		const selectedDate = new Date(date);

		const year = selectedDate.getFullYear();
		const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
		const day = String(selectedDate.getDate()).padStart(2, "0");

		const formattedDate = `${year}-${month}-${day}`;

		const updatedSearchTerms = {
			...searchTermsDate,
			[field]: formattedDate,
		};

		const updatedErrors = { ...errors };

		const from = new Date(updatedSearchTerms.fromDate);
		const to = new Date(updatedSearchTerms.toDate);

		if (field === "toDate") {
			updatedSearchTerms.toDate = formattedDate;

			if (!updatedSearchTerms.fromDate) {
				updatedSearchTerms.fromDate = formattedDate;
			}
		}

		if (updatedSearchTerms.fromDate && updatedSearchTerms.toDate) {
			if (from > to) {
				if (field === "fromDate") {
					updatedErrors.fromDate = "From date cannot be later than To date.";
				} else {
					updatedErrors.toDate = "To date cannot be earlier than From date.";
				}
			} else {
				updatedErrors.fromDate = "";
				updatedErrors.toDate = "";
			}
		}

		setErrors(updatedErrors);
		setSearchTermsDate(updatedSearchTerms);
	};

	const handleClearFromDateSearch = () => {
		setSearchTermsDate((prevTerms) => ({
			...prevTerms,
			fromDate: "",
		}));

		setErrors((prevErrors) => ({
			...prevErrors,
			fromDate: "",
		}));
	};

	const handleClearToDateSearch = () => {
		setSearchTermsDate((prevTerms) => ({
			...prevTerms,
			toDate: "",
		}));

		setErrors((prevErrors) => ({
			...prevErrors,
			toDate: "",
		}));
	};

	const handleDownloadVouchers = async () => {
		if (isLoadingDownload) return;

		setIsLoadingDownload(true);
		setIsSuccess(false);

		try {
			await setDownloadVouchersData(token, dispatch, navigate, vesselId, searchTerms, signal);

			setIsSuccess(true);
		} catch (error) {
			console.error("Download vouchers failed:", error);
		} finally {
			setIsLoadingDownload(false);
		}

		setTimeout(() => setIsSuccess(false), 1500);
	};

	return (
		<div className="grid grid-cols-1 gap-2 md:grid-cols-1">
			{hasVouchers ? (
				<CustomAccordion
					title={
						<div className="flex items-center gap-3">
							{/* <span className="font-bold text-base text-custom-green md:text-base">
								{vesselDetails?.online_vouchers || 0}
							</span> */}
							<p className="font-medium text-white text-xs uppercase tracking-wide md:text-sm">
								Vouchers
							</p>
						</div>
					}
					id="vouchersCustomAccordion"
					isOpen={openAccordions[`total-vouchers`]}
					toggleAccordion={() => toggleAccordion(`total-vouchers`)}
					classCss="border-0 p-2"
					showButton={true}
					customButton={
						<div className="flex gap-2">
							{isvouchersPerDayVisible && (
								<Button
									text={`${!isMobile ? "Download Daily Report" : ""}`}
									color="blue"
									icon={DownloadIcon}
									position="after"
									classNameIcon="w-4 h-4 ms-2 text-white"
									disabled={
										!vesselDetails?.download_report ||
										typeof vesselDetails.download_report !== "string"
									}
									onClick={() => onClickDailyReport()}
								/>
							)}
						</div>
					}
				>
					<div className="!bg-navbar-bg !border-[#272B49] !border-b flex items-center justify-between border-solid p-2 md:rounded-lg">
						<div className="flex flex-col items-center md:items-start">
							<p className="font-bold text-custom-green text-xs uppercase sm:text-sm md:text-base">
								{vesselDetails?.active_vouchers || 0}
							</p>
							<p className="font-medium text-white text-xs uppercase tracking-wide md:text-sm">
								Total Active Vouchers
							</p>
						</div>

						<div className="flex flex-col items-center md:items-start">
							<p className="font-bold text-custom-green text-xs uppercase sm:text-sm md:text-base">
								{vesselDetails?.online_vouchers || 0}
							</p>
							<p className="font-medium text-white text-xs uppercase tracking-wide md:text-sm">
								Total Online Vouchers
							</p>
						</div>
					</div>
					{isvouchersPerDayVisible && (
						<div className="mt-2 bg-navbar-bg md:rounded-lg">
							<div className="flex flex-wrap items-center justify-center">
								<div className="grid gap-2 text-start">
									<p className="truncate font-semibold text-white text-xs uppercase sm:text-sm md:text-normal">{`Vouchers Per Day`}</p>
								</div>
							</div>

							<form className="mt-4">
								<div className="!bg-navbar-bg flex grid-cols-1 gap-1 p-3 md:gap-6 md:rounded-lg lg:grid-cols-4">
									<div className="grid">
										<label
											htmlFor="fromDate"
											className="mb-2 block text-left text-gray-400 text-xs"
										>
											From Date
										</label>
										<DatePicker
											id="fromDate"
											placeholder="Select from date"
											value={searchTermsDate?.fromDate}
											onChange={(date) => handleDateChange(date, "fromDate")}
											onClear={handleClearFromDateSearch}
											weekStart={1}
											positionDatePicker=" z-50 block  absolute mt-2"
											maxDate={searchTermsDate?.toDate && new Date(searchTermsDate.toDate)}
										/>
										{errors.fromDate && (
											<p className="mt-1 text-start text-red-500 text-xs">{errors.fromDate}</p>
										)}
									</div>
									<div className="grid">
										<label htmlFor="toDate" className="mb-2 block text-left text-gray-400 text-xs">
											To Date
										</label>
										<DatePicker
											id="toDate"
											placeholder="Select to date"
											value={searchTermsDate?.toDate}
											onChange={(date) => handleDateChange(date, "toDate")}
											onClear={handleClearToDateSearch}
											weekStart={1}
											minDate={searchTermsDate?.fromDate && new Date(searchTermsDate.fromDate)}
											maxDate={today}
											positionDatePicker="z-50  top-full right-0 absolute mt-2"
										/>
										{errors.toDate && (
											<p className="mt-1 text-start text-red-500 text-xs">{errors.toDate}</p>
										)}
									</div>
								</div>
								{loadingClients ? (
									<div className="flex h-64 items-center justify-center">
										<div className="h-16 w-16 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
									</div>
								) : (
									<div className="">
										{clientdata?.data?.length > 0 ? (
											<div className="mt-2">
												<ClientsChart
													clientdata={clientdata?.data || []}
													filterStartDate={searchTermsDate?.fromDate}
													filterEndDate={searchTermsDate?.toDate}
												/>
											</div>
										) : (
											<span className="flex h-64 items-center justify-center text-center text-gray-500 text-xs">
												No available data
											</span>
										)}
									</div>
								)}
							</form>
						</div>
					)}
					<div
						className={`!bg-navbar-bg !border-[#272B49] !border-t mt-2 grid grid-cols-1 gap-3 border-solid p-3 {${isVisible ? "sm:grid-cols-2 md:grid-cols-2 md:rounded-lg lg:grid-cols-4" : "sm:grid-cols-1 md:grid-cols-1 md:rounded-lg lg:grid-cols-3"}`}
					>
						<div className="grid">
							<SearchInput
								id="search" // must match the key in searchTerms state
								placeholder="Search"
								label="Search by"
								value={searchTerms.search}
								onChange={handleSearchChange}
								onClear={handleClearSearch}
							/>
						</div>
						<div className="grid">
							<SelectInput
								id="online"
								placeholder="Status"
								label={"Filter by"}
								value={searchTerms?.online}
								onChange={handleStatusSearchChange}
								options={statusEnums}
								onClear={handleClearSearch}
								displayField="name"
								marginTop={`${isMobile ? "mt-16" : "mt-20"}`}
								classDropdown={`${isMobile ? "absolute z-20" : "absolute z-40"}`}
								renderOption={(option) => (
									<div className="flex items-center gap-2">
										<span
											className={`h-2 w-2 rounded-full ${
												option.value === 1 ? "bg-green-500" : "bg-red-500"
											}`}
										/>
										{option.name}
									</div>
								)}
							/>
						</div>
						{isVisible && (
							<div className="grid">
								<SelectInput
									id="category"
									placeholder="Category"
									label="Filter by"
									value={searchTerms.category}
									onChange={handleCategorySearchChange}
									options={categoryEnums}
									onClear={handleClearSearch}
									displayField="name"
									marginTop={`${isMobile ? "mt-16" : "mt-20"}`}
									classDropdown={`${isMobile ? "absolute z-20" : "absolute z-40"}`}
								/>
							</div>
						)}
						<div className="flex items-center justify-end">
							<button
								type="button"
								onClick={handleDownloadVouchers}
								className={`mt-4 flex items-center justify-center rounded-xl bg-blue-gray px-3 py-2 font-medium text-sm text-white transition-all duration-300 ease-in-out hover:bg-opacity-40 ${isLoadingDownload || vouchersData?.length === 0 ? "cursor-not-allowed" : ""}
    `}
								disabled={isLoadingDownload || vouchersData?.length === 0}
							>
								<Tooltip
									animation="duration-150"
									content="Export Voucher List"
									className="!bg-dark-blue !bg-opacity-80 m-0 p-1 text-[11px]"
								>
									<div className="relative flex h-5 w-5 items-center justify-center">
										{/* Spinner */}
										<div
											className={`absolute transition-all duration-300 ${
												isLoadingDownload ? "scale-100 opacity-100" : "scale-50 opacity-0"
											}`}
										>
											<div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
										</div>
										{/* Success check */}
										<div
											className={`absolute transition-all duration-300 ${
												isSuccess ? "scale-100 opacity-100" : "scale-50 opacity-0"
											}`}
										>
											✓
										</div>
										<div
											className={`transition-all duration-300 ${
												!isLoadingDownload && !isSuccess
													? "scale-100 opacity-100"
													: "scale-50 opacity-0"
											}`}
										>
											<FileCsvIcon className="h-5 w-5 text-white" />
										</div>
									</div>
								</Tooltip>
							</button>
						</div>
					</div>
					{loading ? (
						<div className="flex h-64 items-center justify-center">
							<div className="h-16 w-16 animate-spin rounded-full border-gray-700 border-t-2 border-b-2"></div>
						</div>
					) : (
						<div className="relative mt-2 overflow-x-auto bg-navbar-bg shadow-md md:rounded-lg">
							<ListTable
								list={vouchersData}
								columns={columns}
								isAction={false}
								handleRowClick={handleRowClick}
								loading={loading}
							/>
							<div className="flex flex-col items-center justify-between gap-2 bg-dark-blue bg-opacity-40 px-2 pt-4 pb-4 text-blue-gray text-xs sm:flex-row sm:gap-0 sm:px-4 sm:text-sm">
								<span>
									Showing {startItemIndex} to {endItemIndex} of {totalItems} entries
								</span>
								<Pagination
									currentPage={currentPage}
									totalPages={totalPages}
									onPageChange={handlePageChange}
								/>
							</div>
						</div>
					)}
				</CustomAccordion>
			) : (
				<div className="flex h-64 items-center justify-center">
					<span className="font-normal text-gray-400 text-xs leading-tight sm:text-sm">
						No data available
					</span>
				</div>
			)}
		</div>
	);
};

export default Vouchers;
